import {
  Card,
  DetailedIconPet,
  FormField,
  FormHelp,
  Grid,
  Heading,
  Label,
  Paragraph,
  Select,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation';
import { Controller, useFormContext } from 'react-hook-form';
import { TEXT_AREA_MAX_LENGTH_510 } from '../../../../constants/form';
import { ASSISTANCE_ANIMALS_URL } from '../../../../constants/urls';
import { get } from 'lodash-es';
import { useEffect } from 'react';
import { FIELD_IDS } from '../../../../constants/wizard';

export function PetsStep() {
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();

  useEffect(() => {
    if (!getValues(FIELD_IDS.SMALL_DOGS)) {
      setValue(FIELD_IDS.SMALL_DOGS, 0);
    }
    if (!getValues(FIELD_IDS.LARGE_DOGS)) {
      setValue(FIELD_IDS.LARGE_DOGS, 0);
    }
    if (!getValues(FIELD_IDS.CATS)) {
      setValue(FIELD_IDS.CATS, 0);
    }
    if (!getValues(FIELD_IDS.PETS_OTHER)) {
      setValue(FIELD_IDS.PETS_OTHER, 0);
    }
    // Limit was changed from 99 to 9
    if (getValues(FIELD_IDS.SMALL_DOGS) > 9) {
      setValue(FIELD_IDS.SMALL_DOGS, 9);
    }
    if (getValues(FIELD_IDS.LARGE_DOGS) > 9) {
      setValue(FIELD_IDS.LARGE_DOGS, 9);
    }
    if (getValues(FIELD_IDS.CATS) > 9) {
      setValue(FIELD_IDS.CATS, 9);
    }
    if (getValues(FIELD_IDS.PETS_OTHER) > 9) {
      setValue(FIELD_IDS.PETS_OTHER, 9);
    }
    // We removed the medium dogs question, set the value to 0 incase there was an old value
    setValue(FIELD_IDS.MEDIUM_DOGS, 0);
  }, []);

  const numSmallDogs = watch(FIELD_IDS.SMALL_DOGS);
  const numLargeDogs = watch(FIELD_IDS.LARGE_DOGS);
  const numCats = watch(FIELD_IDS.CATS);
  const numOther = watch(FIELD_IDS.PETS_OTHER);

  const hasPets = !!(numSmallDogs || numLargeDogs || numCats || numOther);

  useEffect(() => {
    if (!hasPets) {
      setValue(FIELD_IDS.PETS_DESCRIPTION, null);
    }
  }, [hasPets]);

  const errorSmallDogs = get(errors, FIELD_IDS.SMALL_DOGS);
  const errorLargeDogs = get(errors, FIELD_IDS.LARGE_DOGS);
  const errorCats = get(errors, FIELD_IDS.CATS);
  const errorOtherPets = get(errors, FIELD_IDS.PETS_OTHER);

  const parseNumber = (value: string) => {
    const maxLen = 2;
    const number = parseInt(value.replace(/[^0-9]/g, '').substring(0, maxLen) || '0');
    return isNaN(number) ? 0 : number;
  };

  return (
    <>
      <Heading level={4} as="h2" fontFamily="serif">
        How many pets will be living in the home ?
      </Heading>
      <Text>Assistance animals do not need to be listed</Text>

      <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={{ default: '', lg: 'md' }}>
        <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 6' }}>
          <Controller
            name={FIELD_IDS.LARGE_DOGS}
            rules={{ validate: numPetsValidation }}
            render={({ field: { onChange, value } }) => (
              <FormField
                marginTop="sm"
                label={<Label>Large dogs (40+ pounds)</Label>}
                control={
                  <Select
                    value={value ? value : 0}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      onChange(parseNumber(e.target.value));
                    }}
                  >
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                  </Select>
                }
                description={errorLargeDogs && <FormHelp>{errorLargeDogs.message}</FormHelp>}
                error={!!errorLargeDogs}
              />
            )}
          />
        </Grid>
        <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 6' }}>
          <Controller
            name={FIELD_IDS.SMALL_DOGS}
            rules={{ validate: numPetsValidation }}
            render={({ field: { onChange, value } }) => (
              <FormField
                marginTop="sm"
                label={<Label>Small dogs</Label>}
                control={
                  <Select
                    value={value ? value : 0}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      onChange(parseNumber(e.target.value));
                    }}
                  >
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                    <option value={6}>6</option>
                    <option value={7}>7</option>
                    <option value={8}>8</option>
                    <option value={9}>9</option>
                  </Select>
                }
                description={errorSmallDogs && <FormHelp>{errorSmallDogs.message}</FormHelp>}
                error={!!errorSmallDogs}
              />
            )}
          />
        </Grid>
      </Grid>

      <Controller
        name={FIELD_IDS.CATS}
        rules={{ validate: numPetsValidation }}
        render={({ field: { onChange, value } }) => (
          <FormField
            marginTop="sm"
            label={<Label>Cats</Label>}
            control={
              <Select
                value={value ? value : 0}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  onChange(parseNumber(e.target.value));
                }}
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                <option value={6}>6</option>
                <option value={7}>7</option>
                <option value={8}>8</option>
                <option value={9}>9</option>
              </Select>
            }
            description={errorCats && <FormHelp>{errorCats.message}</FormHelp>}
            error={!!errorCats}
          />
        )}
      />

      <Controller
        name={FIELD_IDS.PETS_OTHER}
        rules={{ validate: numPetsValidation }}
        render={({ field: { onChange, value } }) => (
          <FormField
            marginTop="sm"
            label={<Label>Other pets</Label>}
            control={
              <Select
                value={value ? value : 0}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  onChange(parseNumber(e.target.value));
                }}
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
                <option value={6}>6</option>
                <option value={7}>7</option>
                <option value={8}>8</option>
                <option value={9}>9</option>
              </Select>
            }
            description={errorOtherPets && <FormHelp>{errorOtherPets.message}</FormHelp>}
            error={!!errorOtherPets}
          />
        )}
      />

      {hasPets && (
        <FormField
          marginTop="sm"
          label={<Label>Details</Label>}
          control={
            <Textarea
              maxLength={TEXT_AREA_MAX_LENGTH_510}
              resize="vertical"
              {...register(FIELD_IDS.PETS_DESCRIPTION)}
            />
          }
          description={
            <FormHelp>
              Include details such as dog breed, vaccination history, or any pets that aren’t dogs or cats
            </FormHelp>
          }
          optional
        />
      )}
    </>
  );
}

export function PetsStepTip() {
  return (
    <Card cardElevation={0} cardType="secondary">
      <DetailedIconPet marginBottom="sm" />
      <Heading level={4} as="h2" marginBottom="sm" fontFamily="serif">
        Assistance or support animals
      </Heading>
      <Paragraph>
        An assistance or support animal helps a person with a disability or eases the symptoms of a person’s disability.
      </Paragraph>
      <Paragraph marginTop="sm">
        Under federal law, assistance animals are not considered pets. In some cases, housing providers must allow
        assistance animals if there is a disability-related need.
      </Paragraph>
      <TextButton href={ASSISTANCE_ANIMALS_URL} as="a" target="_blank" marginTop="sm">
        Learn more
      </TextButton>
    </Card>
  );
}

const numPetsValidation = (value: number) => {
  if (!value || (Number.isInteger(value) && value >= 0)) {
    return true;
  } else if (!Number.isInteger(value)) {
    return 'Enter a number without any decimals or fractions';
  } else {
    return 'Minimum value is 0';
  }
};
